import { FC, useMemo, useState } from "react";
import { Header, Icon, Button, Dialog, useCustomNavigate } from "@onmo/ui";
import { map } from "lodash-es";
import {
  UserSubscriptionStatus,
  User,
  getUserB2bSubscriptionGQL,
} from "@onmo/graphql/graphql";
import { useQuery } from "@apollo/client";
import { logout } from "@onmo/auth";
import { useTranslation } from "react-i18next";

import { PwaBanner } from "../../components/common/PwaBanner";
import { MenuCategory } from "../../components/common/MenuCategory";
import { PageRoutes } from "../../constants/routes";
import { HelpShift } from "../../components/common/HelpShift";
import { useStyles } from "./Settings.style";

export const Settings: FC = () => {
  const classes = useStyles();
  const { navigate, handleBack } = useCustomNavigate();
  const { data: dataSubscription } = useQuery<{ me: User }>(
    getUserB2bSubscriptionGQL,
  );
  const { t } = useTranslation();

  const menus = useMemo(() => {
    const subscription = dataSubscription?.me?.subscription;
    const subscriptionItems = [];
    if (subscription) {
      if (subscription.status === UserSubscriptionStatus.Subscribed) {
        subscriptionItems.push({
          icon: <Icon name="icon-wallet-check" />,
          title: t("common_My Subscription"),
          onAction: () => {
            subscription?.managementUrl &&
              window.open(subscription?.managementUrl, "_self");
          },
        });
        subscriptionItems.push({
          icon: <Icon name="icon-wallet-check" />,
          title: t("setting_Unsubscribe"),
          onAction: () => {
            subscription?.redirectUrl &&
              window.open(subscription?.redirectUrl, "_self");
          },
        });
      }
    }

    return [
      {
        category: t("account"),
        value: [
          {
            icon: <Icon name="icon-linear-global" />,
            title: t("languages"),
            onAction: () => navigate(PageRoutes.Language),
          },
          ...subscriptionItems,
        ],
      },
      {
        category: t("support"),
        value: [
          {
            icon: <Icon name="icon-message-question" />,
            title: t("faq"),
            onAction: () => navigate(PageRoutes.Faq),
          },
          {
            icon: <Icon name="icon-shield-security" />,
            title: t("privacy_policy"),
            onAction: () => navigate(PageRoutes.PrivacyPolicy),
          },
          {
            icon: <Icon name="icon-document" />,
            title: t("terms_conditions"),
            onAction: () => navigate(PageRoutes.TermAndCondition),
          },
          {
            icon: <Icon name="icon-linear-sms" />,
            title: t("help"),
            onAction: () => setIsOpenHelpShift(true),
          },
        ],
      },
    ];
  }, [t, navigate, dataSubscription?.me?.subscription]);

  const [isOpenHelpShift, setIsOpenHelpShift] = useState(false);
  const [isOpenLogout, setIsOpenLogout] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const onToggleLogout = () => {
    if (isLoading) return;
    setIsOpenLogout(!isOpenLogout);
  };

  const onLogout = async () => {
    setIsLoading(true);
    try {
      await logout();
      localStorage.removeItem("hasOnboarded");
    } catch (error) {
      console.error(error);
    }
    setIsLoading(false);
  };

  return (
    <div>
      <Header
        title={t("settings")}
        leftElement={
          <Icon name="icon-bold-arrow-left-3" onClick={() => handleBack()} />
        }
      />
      <div className={classes.content}>
        <PwaBanner />
        <div>
          {map(menus, (m, idx) => (
            <MenuCategory
              key={idx}
              title={m.category}
              menus={m.value}
              className={classes.category}
            />
          ))}
        </div>
      </div>
      <div className={classes.bottom}>
        <Button variant="outlined" fullWidth onClick={onToggleLogout}>
          {t("logout")}
        </Button>
      </div>
      {isOpenHelpShift && <HelpShift onClose={setIsOpenHelpShift} />}
      <Dialog
        open={isOpenLogout}
        title={t("logout_title")}
        description={t("logout_description")}
        mainAction={{ text: t("not_now"), onClick: onToggleLogout }}
        secondAction={{
          text: t("logout"),
          onClick: onLogout,
          loading: isLoading,
        }}
      />
    </div>
  );
};
